import { v4 as uuidv4 } from "uuid";

import {
  AccessAction,
  AccessRuleMode,
  AccessRuleStatus
} from "@/types/table-access";

import { InputAccessConfig, InputAccessRule } from "@/types/input-access";

export class InputAccessManager {
  static serialize(config: InputAccessConfig): string {
    const compact = {
      v: 1,
      r: config.rules.map((rule) => {
        const compactRule: Record<string, unknown> = {
          n: rule.name,
          m: rule.mode === AccessRuleMode.ROLE ? 0 : rule.mode === AccessRuleMode.USER ? 2 : 1,
          a: rule.action === AccessAction.SHOW ? 1 : 0,
          s: rule.status === AccessRuleStatus.INACTIVE ? 0 : 1
        };

        if (rule.mode === AccessRuleMode.ROLE) {
          if (rule.allRoles) {
            compactRule.al = 1;
          } else if (rule.roleIds.length > 0) {
            compactRule.r = rule.roleIds;
          }
        } else if (rule.mode === AccessRuleMode.USER) {
          if (rule.userIds.length > 0) {
            compactRule.u = rule.userIds;
          }
        } else if (rule.query.trim()) {
          compactRule.q = rule.query;
        }

        return compactRule;
      })
    };

    return JSON.stringify(compact);
  }

  static deserialize(jsonString: string | null): InputAccessConfig | null {
    if (!jsonString) return null;

    try {
      const parsed: unknown = JSON.parse(jsonString);
      const asAny = parsed as any;

      if (asAny && typeof asAny === "object" && "v" in asAny && "r" in asAny) {
        const rules: InputAccessRule[] = (Array.isArray(asAny.r) ? asAny.r : []).map(
          (rule: any, index: number) => {
            const mode: AccessRuleMode =
              rule?.m === 2
                ? AccessRuleMode.USER
                : rule?.m === 1
                  ? AccessRuleMode.QUERY
                  : AccessRuleMode.ROLE;
            const status: AccessRuleStatus =
              rule?.s === 0 ? AccessRuleStatus.INACTIVE : AccessRuleStatus.ACTIVE;

            return {
              id: uuidv4(),
              name:
                typeof rule?.n === "string" && rule.n.trim()
                  ? rule.n
                  : `قانون ${index + 1}`,
              mode,
              allRoles: rule?.al === 1,
              roleIds: Array.isArray(rule?.r)
                ? rule.r.filter((value: unknown) => typeof value === "number")
                : [],
              userIds: Array.isArray(rule?.u)
                ? rule.u.filter((value: unknown) => typeof value === "number")
                : [],
              query: typeof rule?.q === "string" ? rule.q : "",
              action: rule?.a === 0 ? AccessAction.HIDE : AccessAction.SHOW,
              status
            };
          }
        );

        return {
          version: 1,
          inputId: typeof asAny.i === "string" ? asAny.i : "",
          rules
        };
      }

      if (asAny && typeof asAny === "object" && Array.isArray(asAny.rules)) {
        const legacyRules: InputAccessRule[] = asAny.rules.map(
          (legacyRule: any, index: number) => {
            const mode: AccessRuleMode =
              legacyRule?.ruleType === "QUERY_BASED"
                ? AccessRuleMode.QUERY
                : AccessRuleMode.ROLE;

            return {
              id: uuidv4(),
              name:
                typeof legacyRule?.name === "string" && legacyRule.name.trim()
                  ? legacyRule.name
                  : `قانون ${index + 1}`,
              mode,
              allRoles: false,
              roleIds: [],
              query: typeof legacyRule?.query === "string" ? legacyRule.query : "",
              action:
                legacyRule?.action === AccessAction.HIDE
                  ? AccessAction.HIDE
                  : AccessAction.SHOW,
              status:
                legacyRule?.isActive === false
                  ? AccessRuleStatus.INACTIVE
                  : AccessRuleStatus.ACTIVE
            };
          }
        );

        return {
          version: 1,
          inputId: typeof asAny.inputId === "string" ? asAny.inputId : "",
          rules: legacyRules
        };
      }

      return null;
    } catch {
      return null;
    }
  }

  static createRule(name: string): InputAccessRule {
    return {
      id: uuidv4(),
      name,
      mode: AccessRuleMode.ROLE,
      allRoles: false,
      roleIds: [],
      userIds: [],
      query: "",
      action: AccessAction.SHOW,
      status: AccessRuleStatus.ACTIVE
    };
  }

  static createConfig(inputId: string): InputAccessConfig {
    return {
      version: 1,
      inputId,
      rules: []
    };
  }

  static addRule(config: InputAccessConfig, rule: InputAccessRule): InputAccessConfig {
    return {
      ...config,
      rules: [...config.rules, rule]
    };
  }

  static updateRule(
    config: InputAccessConfig,
    ruleId: string,
    updates: Partial<InputAccessRule>
  ): InputAccessConfig {
    return {
      ...config,
      rules: config.rules.map((rule) =>
        rule.id === ruleId ? { ...rule, ...updates } : rule
      )
    };
  }

  static removeRule(config: InputAccessConfig, ruleId: string): InputAccessConfig {
    return {
      ...config,
      rules: config.rules.filter((rule) => rule.id !== ruleId)
    };
  }

  static getRule(config: InputAccessConfig, ruleId: string): InputAccessRule | undefined {
    return config.rules.find((rule) => rule.id === ruleId);
  }

  static isValidRule(rule: InputAccessRule): boolean {
    if (!rule.name?.trim()) return false;

    if (rule.mode === AccessRuleMode.ROLE) {
      return rule.allRoles || rule.roleIds.length > 0;
    }

    if (rule.mode === AccessRuleMode.USER) {
      return rule.userIds.length > 0;
    }

    return !!rule.query.trim();
  }
}